import React, { useContext, useState, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  Avatar,
  TextField,
  IconButton,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Divider,
  InputAdornment,
  useMediaQuery,
  CircularProgress,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SearchIcon from '@mui/icons-material/Search';
import EmojiEmotionsIcon from '@mui/icons-material/EmojiEmotions';
import SendIcon from '@mui/icons-material/Send';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import { AuthContext } from '../../contexts/AuthContext';
import axios from 'axios';
import { toast } from 'react-toastify';
import { useSocket } from '../../contexts/SocketContext';
import ErrorMessage from '../ErrorMessage';
import CircularProgressLoading from '../CircularProgress';

// Chat component for a messaging interface
const Chat = () => {
  const { user } = useContext(AuthContext);
  const { socket } = useSocket();
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('Editors');
  const [selectedChat, setSelectedChat] = useState(null);
  const [mobileView, setMobileView] = useState('chats');
  const [allChats, setAllChats] = useState([]);
  const [selectedChatMessages, setSelectedMessages] = useState([]);
  const [text, setText] = useState('');
  const [isSendingMessage, setIsSendingMessage] = useState(false);
  const isSmallDevice = useMediaQuery('(max-width:1300px)');
  const messagesEndRef = useRef(null);

  const [error ,setError] = useState("")

  // Scroll to bottom of messages
  const scrollToBottom = () => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  };

  // Handle chat selection
  const handleChatSelect = (chat) => {
    if (!chat) return;
    console.log('Selecting Chat', chat);
    setSelectedChat(chat);
    setSelectedMessages(chat?.messages || []);
    if (isSmallDevice) {
      setMobileView('messages');
    }
    setTimeout(scrollToBottom, 100); // Delay to ensure DOM is updated
  };

  // Convert timestamp to time format
  const convertIntoTimeFormat = (dateTimeString) => {
    if (!dateTimeString) return 'Unknown time';
    try {
      const date = new Date(dateTimeString);
      if (isNaN(date.getTime())) return 'Invalid time';
      return date.toLocaleTimeString('en-US', {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true,
      });
    } catch {
      return 'Error formatting time';
    }
  };

  // Handle back to chats on mobile
  const handleBackToChats = () => {
    setMobileView('chats');
    setSelectedChat(null);
    setSelectedMessages([]);
  };

  // Send a message
  const sendMessage = async () => {
    if (!text.trim()) {
      toast.error('Message cannot be empty');
      return;
    }
    if (!user?.id || !selectedChat?.otherUser?._id || !selectedChat?._id) {
      toast.error('Cannot send message: Invalid user or chat data');
      return;
    }

    setIsSendingMessage(true);
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_BASE_URL || 'http://localhost:5000'}/chats/send-message`,
        {
          senderId: user.id,
          senderRole: 'student',
          receiverId: selectedChat.otherUser._id,
          text,
          chatId: selectedChat._id,
        }
      );

      const newMessage = response.data;
      if (!newMessage) throw new Error('No message returned from server');

      // Update lastMessage in allChats
      setAllChats((prevChats) =>
        prevChats.map((chat) =>
          chat._id === selectedChat._id
            ? { ...chat, lastMessage: newMessage }
            : chat
        )
      );

      // Add new message to selectedChatMessages
      setSelectedMessages((prevMessages) => [...prevMessages, newMessage]);

      // Scroll to bottom after sending message
      setTimeout(scrollToBottom, 100);

      setText(''); // Clear input
    } catch (e) {
      console.error('Send message error:', e);
      toast.error(
        e.response?.data?.message || 'Failed to send message. Please try again.'
      );
    } finally {
      setIsSendingMessage(false);
    }
  };

  // Fetch all chats
  const getAllChats = async () => {
    if (!user?.id) {
      toast.error('User not authenticated');
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const response = await axios.get(
        `${
          process.env.REACT_APP_BACKEND_BASE_URL || 'http://localhost:5000'
        }/chats/all-chats/${user.id}/student`
      );

      const chats = response.data?.chats || [];
      setAllChats(chats);
    } catch (e) {
      
      setError(
        e.response?.data?.message || 'Failed to load chats. Please try again.'
      );
    } finally {
      setLoading(false);
    }
  };

  // Handle mobile view switching
  useEffect(() => {
    if (isSmallDevice) {
      if (selectedChat) {
        setMobileView('messages');
      } else {
        setMobileView('chats');
      }
    } else {
      setMobileView('chats');
    }
  }, [isSmallDevice, selectedChat]);

  // Fetch chats on mount
  useEffect(() => {
    getAllChats();
  }, [user]);

  // Handle socket events
  useEffect(() => {
    if (!socket) {
      console.warn('Socket not initialized');
      return;
    }

    socket.on('new-message', (message) => {
      if (!message || !message.chat) {
        console.warn('Invalid message received:', message);
        return;
      }

      console.log('New Message Received', message);

      // Update selectedChatMessages if message belongs to current chat
      if (selectedChat && message.chat === selectedChat._id) {
        setSelectedMessages((prevMessages) => [...prevMessages, message]);
        setTimeout(scrollToBottom, 100);
      }

      let chatExists = false;

      // Update lastMessage in allChats
      setAllChats((prevChats) => {
        const updatedChats = prevChats.map((chat) => {
          if (chat._id.toString() === message.chat.toString()) {
            chatExists = true;
            return {
              ...chat,
              lastMessage: message,
              messages: chat.messages
                ? [...chat.messages, message]
                : [message],
            };
          }
          return chat;
        });
        return updatedChats;
      });

      // Fetch all chats if chat doesn't exist
      if (!chatExists) {
        getAllChats();
      }
    });

    socket.on('error', (error) => {
      console.error('Socket error:', error);
      toast.error('Connection error. Please check your network.');
    });

    return () => {
      socket.off('new-message');
      socket.off('error');
    };
  }, [socket, selectedChat]);
  if(error){
    return(<ErrorMessage message={error} onTryAgain={getAllChats}/>)
  }

  return (
    <Box
      sx={{
        display: 'flex',
        height: '100vh',
        bgcolor: '#f9f9f9',
        padding: 0,
        gap: 2,
        flexDirection: isSmallDevice ? 'column' : 'row',
      }}
    >
      {/* Left Section */}
      {(!isSmallDevice || mobileView === 'chats') && (
        <Box
          sx={{
            width: isSmallDevice ? '100%' : '30%',
            bgcolor: '#F7F8FA',
            borderRadius: 2,
            padding: 2,
            display:
              isSmallDevice && mobileView === 'messages' ? 'none' : 'block',
          }}
        >
          <Box
            sx={{ display: 'flex', justifyContent: 'center', marginBottom: 2 }}
          >
            <TextField
              placeholder="Search chats"
              variant="outlined"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
              sx={{
                bgcolor: '#fff',
                width: '100%',
                borderRadius: '50px',
                height: 40,
                '& .MuiOutlinedInput-root': {
                  borderRadius: '50px',
                  height: '100%',
                  padding: '5px 12px',
                  '& fieldset': {
                    border: 'none',
                  },
                  '&:hover fieldset': {
                    border: 'none',
                  },
                  '&.Mui-focused fieldset': {
                    border: 'none',
                  },
                },
                '& .MuiInputBase-input': {
                  fontSize: '14px',
                },
              }}
            />
          </Box>

          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              gap: 2,
              marginBottom: 2,
            }}
          >
            {['Editors', 'Writers'].map((tab) => (
              <Typography
                key={tab}
                onClick={() => setActiveTab(tab)}
                sx={{
                  cursor: 'pointer',
                  padding: '4px 12px',
                  borderRadius: '50px',
                  width: '115px',
                  fontSize: '13px',
                  border: `2px solid ${
                    activeTab === tab ? 'orange' : 'transparent'
                  }`,
                  color: activeTab === tab ? '#000' : '#000',
                  textAlign: 'center',
                  transition: 'all 0.3s ease',
                }}
              >
                {tab}
              </Typography>
            ))}
          </Box>

          <Divider />

          <Box
            sx={{
              maxHeight: 'calc(100vh - 200px)',
              overflowY: 'auto',
              display: 'flex',
              flexDirection: 'column',
              '&::-webkit-scrollbar': {
                width: '8px',
              },
              '&::-webkit-scrollbar-thumb': {
                backgroundColor: '#c4c4c4',
                borderRadius: '50px',
                border: '2px solid transparent',
                backgroundClip: 'content-box',
              },
              '&::-webkit-scrollbar-thumb:hover': {
                backgroundColor: '#a0a0a0',
              },
              '&::-webkit-scrollbar-track': {
                backgroundColor: '#f0f0f0',
                borderRadius: '50px',
              },
            }}
          >
            {loading ? (
             
                <CircularProgressLoading/>
              
            ) : allChats.length === 0 ? (
              <Typography
                variant="body1"
                sx={{
                  textAlign: 'center',
                  color: 'gray',
                  fontWeight: 'bold',
                  mt: 2,
                }}
              >
                No Chats
                </Typography>
            ) : (
              allChats
                .filter((chat) => {
                  if (!chat?.otherUserRole) return false;
                  if (activeTab === 'Writers') {
                    return chat.otherUserRole === 'writer';
                  } else if (activeTab === 'Students') {
                    return chat.otherUserRole === 'student';
                  }
                  return true;
                })
                .map((chat) => (
                  <ListItem
                    key={chat?._id || 'unknown'}
                    button
                    onClick={() => handleChatSelect(chat)}
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      borderRadius: 2,
                      bgcolor:
                        selectedChat?._id === chat?._id
                          ? 'rgba(8, 126, 138, 0.1)'
                          : 'transparent',
                      marginBottom: 1,
                      padding: 1,
                      borderLeft:
                        selectedChat?._id === chat?._id
                          ? '3px solid #0b3953'
                          : '',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        bgcolor: 'rgba(8, 126, 138, 0.1)',
                      },
                    }}
                  >
                    <Typography
                      variant="subtitle2"
                      sx={{
                        color: 'black',
                        cursor: 'pointer',
                        fontWeight: 'bold',
                        fontSize: '12px',
                        mb: 0.5,
                      }}
                    >
                      {chat?.order?.typeOfPaper || 'Unknown Paper'}
                    </Typography>

                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        width: '100%',
                        mb: 0.5,
                      }}
                    >
                      <Typography
                        variant="body2"
                        sx={{
                          color: 'gray',
                          cursor: 'pointer',
                          fontSize: '10px',
                        }}
                      >
                        {chat?.order?._id || 'No Order ID'}
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{
                          color: '#888',
                          cursor: 'pointer',
                          fontSize: '9px',
                        }}
                      >
                        {convertIntoTimeFormat(
                          chat?.lastMessage?.timestamp ||
                            chat?.lastMessage?.createdAt
                        )}
                      </Typography>
                    </Box>

                    <Box
                      sx={{ display: 'flex', alignItems: 'center', width: '100%' }}
                    >
                      <ListItemAvatar>
                        <Avatar
                          sx={{
                            bgcolor: 'darkcyan',
                            cursor: 'pointer',
                            height: '50px',
                            width: '50px',
                          }}
                        >
                          {chat?.otherUserRole === 'student' ? 'S' : 'W'}
                        </Avatar>
                      </ListItemAvatar>
                      <Box sx={{ flex: 1, ml: 1 }}>
                        <Typography
                          variant="body2"
                          sx={{
                            wordBreak: 'break-word',
                            color: chat?.unread ? '#0b3953' : '#A0A6B1',
                            cursor: 'pointer',
                            fontSize: '10px',
                          }}
                        >
                          {chat?.lastMessage?.text || 'No message'}
                        </Typography>
                      </Box>
                      {chat?.unread && (
                        <Box
                          sx={{
                            bgcolor: 'orange',
                            color: 'white',
                            borderRadius: '50%',
                            width: 16,
                            height: 16,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            ml: 1,
                          }}
                        >
                          {chat.unread}
                        </Box>
                      )}
                    </Box>
                  </ListItem>
                ))
            )}
          </Box>
        </Box>
      )}

      {/* Right Section */}
      {(!isSmallDevice || mobileView === 'messages') && (
        <Box
          sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            bgcolor: 'white',
            borderRadius: 0,
            padding: 2,
            '&::-webkit-scrollbar': {
              width: '8px',
            },
            '&::-webkit-scrollbar-thumb': {
              backgroundColor: '#c4c4c4',
              borderRadius: '50px',
              border: '2px solid transparent',
              backgroundClip: 'content-box',
            },
            '&::-webkit-scrollbar-thumb:hover': {
              backgroundColor: '#a0a0a0',
            },
            '&::-webkit-scrollbar-track': {
              backgroundColor: '#f0f0f0',
              borderRadius: '50px',
            },
          }}
        >
          {selectedChat ? (
            <>
              <Box
                sx={{
                  flex: 1,
                  padding: 1.5,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 1.5,
                  overflow: 'auto',
                  '&::-webkit-scrollbar': {
                    width: '8px',
                  },
                  '&::-webkit-scrollbar-thumb': {
                    backgroundColor: '#c4c4c4',
                    borderRadius: '50px',
                    border: '2px solid transparent',
                    backgroundClip: 'content-box',
                  },
                  '&::-webkit-scrollbar-thumb:hover': {
                    backgroundColor: '#a0a0a0',
                  },
                  '&::-webkit-scrollbar-track': {
                    backgroundColor: '#f0f0f0',
                    borderRadius: '50px',
                  },
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'row',
                    alignItems: 'center',
                    gap: 1.5,
                    paddingBottom: 1.5,
                    borderBottom: '1px solid #e0e0e0',
                  }}
                >
                  {isSmallDevice && (
                    <IconButton onClick={handleBackToChats}>
                      <ArrowBackIcon />
                    </IconButton>
                  )}
                  <Box>
                    <Typography
                      variant="h1"
                      fontSize={13}
                      fontWeight="bold"
                    >
                      {selectedChat?.order?.typeOfPaper || 'Unknown Paper'}
                    </Typography>
                    <Typography sx={{ color: '#A0A6B1', fontSize: 12 }}>
                      {`Order # ${selectedChat?.order?._id || 'No Order ID'}`}
                    </Typography>
                  </Box>
                </Box>
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 1.5,
                  }}
                >
                  {selectedChatMessages.map((msg) => (
                    <Box
                      key={msg?._id || 'unknown'}
                      sx={{
                        alignSelf:
                          msg?.sender?.userId === user?.id
                            ? 'flex-end'
                            : 'flex-start',
                        width: '70%',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems:
                          msg?.sender?.userId === user?.id
                            ? 'flex-end'
                            : 'flex-start',
                      }}
                    >
                      <Box
                        sx={{
                          bgcolor:
                            msg?.sender?.userId === user?.id
                              ? '#E7F3F4'
                              : '#F7F8FA',
                          color:
                            msg?.sender?.userId === user?.id
                              ? 'black'
                              : 'black',
                          padding: 1.5,
                          width: isSmallDevice ? '90%' : '665px',
                          height: isSmallDevice ? 'auto' : '150px',
                          borderRadius: 2,
                        }}
                      >
                        <Typography sx={{ fontSize: 12 }}>
                          {msg?.text || 'No message content'}
                        </Typography>
                      </Box>
                      <Typography
                        variant="caption"
                        sx={{
                          color: '#888',
                          marginTop: 0.5,
                          fontSize: 10,
                        }}
                      >
                        {convertIntoTimeFormat(
                          msg?.createdAt || msg?.timestamp
                        )}
                      </Typography>
                    </Box>
                  ))}
                  <div ref={messagesEndRef} />
                </Box>
              </Box>

              {/* Send Message Section */}
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                  mb: 7,
                  padding: 1.5,
                  bgcolor: 'white',
                  borderTop: '1px solid #e0e0e0',
                }}
              >
                <IconButton sx={{ fontSize: 20 }}>
                  <AttachFileIcon />
                </IconButton>
                <TextField
                  fullWidth
                  value={text}
                  onChange={(e) => setText(e.target.value)}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter' && !event.shiftKey) {
                      event.preventDefault();
                      sendMessage();
                    }
                  }}
                  placeholder="Type a message"
                  variant="outlined"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '50px',
                      '& fieldset': {
                        border: '1px solid #e0e0e0',
                      },
                      '&:hover fieldset': {
                        border: '1px solid #a0a0a0',
                      },
                      '&.Mui-focused fieldset': {
                        border: '2px solid #a0a0a0',
                      },
                    },
                    fontSize: 14,
                  }}
                />
                <IconButton sx={{ fontSize: 20 }}>
                  <EmojiEmotionsIcon />
                </IconButton>
                <IconButton
                  onClick={sendMessage}
                  disabled={isSendingMessage}
                  color="primary"
                  sx={{ fontSize: 20 }}
                >
                  {isSendingMessage ? (
                    <CircularProgress
                      sx={{ height: '10px', width: '10px', color: 'gray' }}
                      thickness={7}
                    />
                  ) : (
                    <SendIcon />
                  )}
                </IconButton>
              </Box>
            </>
          ) : (
            <Typography
              sx={{ textAlign: 'center', marginTop: 'auto', marginBottom: 'auto' }}
            >
              Select a chat to start messaging
            </Typography>
          )}
        </Box>
      )}
    </Box>
  );
};

export default Chat;